'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import PageHeader from '@/components/shared/PageHeader';
import FilterRow from '@/components/shared/FilterRow';
import EmptyState from '@/components/shared/EmptyState';
import { Skeleton } from '@/components/shared/Skeletons';
import { mockResources, Resource } from '@/data/mockResources';
import { BookOpen, ArrowRight, MapPin, Home, Gift, Star } from 'lucide-react';

// Extended resource hub categories including new lifestyle sections
const hubCategories = [
  'All',
  'Getting Started',
  'Trust & Safety',
  'Community Channels',
  'About This Platform',
  'Lifestyle & Living',
  'Real Estate',
];

const categoryFilters = hubCategories.map((c) => ({ label: c, value: c === 'All' ? 'all' : c }));

const categoryColorMap: Record<string, string> = {
  'Getting Started': '#4CC9FF',
  'Trust & Safety': '#2DD4A0',
  'Community Channels': '#9B7FFF',
  'About This Platform': '#F0A832',
  'Lifestyle & Living': '#FF6B9D',
  'Real Estate': '#4CC9FF',
};

// Dedicated lifestyle/utility resource cards (new routes)
interface HubCard {
  id: string;
  title: string;
  description: string;
  href: string;
  icon: string;
  color: string;
  category: string;
  badge?: string;
}

const lifestyleHubCards: HubCard[] = [
  {
    id: 'hub-locals-guide',
    title: 'Locals Guide',
    description: 'A curated insider guide to the best of Las Vegas — dining, nightlife, wellness, fitness, and hidden gems through a resident lens.',
    href: '/resources/locals-guide',
    icon: '🗺️',
    color: '#4CC9FF',
    category: 'Lifestyle & Living',
  },
  {
    id: 'hub-panorama-living',
    title: 'Panorama Living',
    description: 'An insider handbook for elevated living at Panorama Towers. Practical tips, household upgrades, travel-away checklists, and daily habits.',
    href: '/resources/panorama-living',
    icon: '🏙️',
    color: '#9B7FFF',
    category: 'Lifestyle & Living',
  },
  {
    id: 'hub-perks',
    title: 'Perks & Privileges',
    description: 'Resident perks, preferred access, and curated partnerships for Panorama residents. Nightlife, dining, wellness, and more.',
    href: '/resources/perks',
    icon: '🎁',
    color: '#2DD4A0',
    category: 'Lifestyle & Living',
    badge: 'New',
  },
  {
    id: 'hub-real-estate',
    title: 'Real Estate',
    description: 'Panorama-specific listings, rentals, market context, owner resources, and a specialist spotlight.',
    href: '/resources/real-estate',
    icon: '🏢',
    color: '#F0A832',
    category: 'Real Estate',
    badge: 'New',
  },
];

// Additional utility links (external/informational)
const utilityLinks: HubCard[] = [];

function ResourceCardSkeleton() {
  return (
    <div className="neon-card p-5">
      <div className="flex items-start gap-3 mb-3">
        <Skeleton className="w-10 h-10 rounded-lg flex-shrink-0" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-3 w-24 rounded-full" />
        </div>
      </div>
      <Skeleton className="h-3 w-full mb-1.5" />
      <Skeleton className="h-3 w-4/5 mb-4" />
      <Skeleton className="h-4 w-20" />
    </div>
  );
}

function HubCard({ card }: { card: HubCard }) {
  return (
    <Link href={card.href} className="block group focus-visible:outline-none">
      <div className="neon-card p-5 h-full group-focus-visible:border-[rgba(76,201,255,0.35)] group-focus-visible:shadow-[0_0_0_3px_rgba(76,201,255,0.15)]">
        <div className="flex items-start gap-3 mb-3">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 text-[18px]"
            style={{ background: `${card.color}14`, border: `1px solid ${card.color}28` }}
          >
            {card.icon}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-display font-semibold text-[14px] leading-snug" style={{ color: '#F2F4FF' }}>
                {card.title}
              </h3>
              {card.badge && (
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wide"
                  style={{ background: `${card.color}14`, color: card.color, border: `1px solid ${card.color}28` }}
                >
                  {card.badge}
                </span>
              )}
            </div>
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide"
              style={{ background: `${card.color}14`, border: `1px solid ${card.color}28`, color: card.color }}
            >
              {card.category}
            </span>
          </div>
        </div>
        <p className="text-[13px] leading-relaxed mb-4 line-clamp-2" style={{ color: '#A7A3C4' }}>
          {card.description}
        </p>
        <span className="flex items-center gap-1 text-[12px] font-medium transition-colors duration-150 group-hover:opacity-80" style={{ color: card.color }}>
          Explore <ArrowRight size={11} />
        </span>
      </div>
    </Link>
  );
}

function ResourceCard({ resource }: { resource: Resource }) {
  const color = categoryColorMap[resource.category] || '#4CC9FF';
  return (
    <Link href={`/resources/${resource.slug}`} className="block group focus-visible:outline-none">
      <div className="neon-card p-5 h-full group-focus-visible:border-[rgba(76,201,255,0.35)] group-focus-visible:shadow-[0_0_0_3px_rgba(76,201,255,0.15)]">
        <div className="flex items-start gap-3 mb-3">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 text-[18px]"
            style={{ background: `${color}14`, border: `1px solid ${color}28` }}
          >
            {resource.icon}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-semibold text-[14px] leading-snug mb-1" style={{ color: '#F2F4FF' }}>
              {resource.title}
            </h3>
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide"
              style={{ background: `${color}14`, border: `1px solid ${color}28`, color }}
            >
              {resource.category}
            </span>
          </div>
        </div>
        <p className="text-[13px] leading-relaxed mb-4 line-clamp-2" style={{ color: '#A7A3C4' }}>
          {resource.excerpt}
        </p>
        <span className="flex items-center gap-1 text-[12px] font-medium transition-colors duration-150 group-hover:opacity-80" style={{ color }}>
          Explore <ArrowRight size={11} />
        </span>
      </div>
    </Link>
  );
}

export default function ResourcesPage() {
  const [activeFilter, setActiveFilter] = useState('all');
  const [isLoading] = useState(false);

  const allHubCards = [...lifestyleHubCards, ...utilityLinks];

  const filteredHubCards = allHubCards.filter((c) =>
    activeFilter === 'all' || c.category === activeFilter
  );

  const filteredResources = mockResources.filter((r) =>
    activeFilter === 'all' || r.category === activeFilter
  );

  const showLifestyle = activeFilter === 'all' || activeFilter === 'Lifestyle & Living' || activeFilter === 'Real Estate';
  const showCommunity = activeFilter === 'all' || activeFilter === 'Community Channels';
  const showTrustSafety = activeFilter === 'all' || activeFilter === 'Trust & Safety';
  const showGettingStarted = activeFilter === 'all' || activeFilter === 'Getting Started';
  const showAbout = activeFilter === 'all' || activeFilter === 'About This Platform';

  const totalCount = mockResources.length + lifestyleHubCards.length;

  return (
    <div className="min-h-screen" style={{ background: '#07060C' }}>
      <SiteHeader theme="dark" />
      <main className="pt-16">
        <PageHeader
          eyebrow="Private Community"
          title="Resources"
          description="Guides, lifestyle content, policies, and community information for Panorama Towers residents. Your complete content center."
          stat={{ value: String(totalCount), label: 'resources' }}
        />

        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-8">
          <div className="mb-8">
            <FilterRow filters={categoryFilters} active={activeFilter} onChange={setActiveFilter} />
          </div>

          {isLoading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {Array.from({ length: 8 }).map((_, i) => <ResourceCardSkeleton key={i} />)}
            </div>
          )}

          {!isLoading && filteredHubCards.length === 0 && filteredResources.length === 0 && (
            <EmptyState
              title="No resources found"
              description="Try a different category filter."
              icon={<BookOpen size={22} style={{ color: '#4CC9FF' }} />}
              action={
                <button className="btn-secondary text-[13px] px-4 py-2" onClick={() => setActiveFilter('all')}>
                  Show all resources
                </button>
              }
            />
          )}

          {!isLoading && (
            <div className="space-y-12">

              {/* Lifestyle & Living section */}
              {showLifestyle && (
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <Star size={16} style={{ color: '#FF6B9D' }} />
                    <h2 className="font-display font-semibold text-[17px]" style={{ color: '#F2F4FF' }}>
                      Lifestyle & Living
                    </h2>
                    <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.06)' }} />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {lifestyleHubCards
                      .filter((c) => activeFilter === 'all' || c.category === activeFilter || (activeFilter === 'Real Estate' && c.category === 'Real Estate'))
                      .map((card) => (
                        <HubCard key={card.id} card={card} />
                      ))}
                  </div>
                </div>
              )}

              {/* Getting Started */}
              {showGettingStarted && (
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <BookOpen size={16} style={{ color: '#4CC9FF' }} />
                    <h2 className="font-display font-semibold text-[17px]" style={{ color: '#F2F4FF' }}>
                      Getting Started
                    </h2>
                    <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.06)' }} />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {mockResources
                      .filter((r) => r.category === 'Getting Started')
                      .map((resource) => (
                        <ResourceCard key={resource.id} resource={resource} />
                      ))}
                  </div>
                </div>
              )}

              {/* Trust & Safety */}
              {showTrustSafety && (
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <Home size={16} style={{ color: '#2DD4A0' }} />
                    <h2 className="font-display font-semibold text-[17px]" style={{ color: '#F2F4FF' }}>
                      Trust & Safety
                    </h2>
                    <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.06)' }} />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {[
                      ...mockResources.filter((r) => r.category === 'Trust & Safety'),
                      ...utilityLinks.filter((u) => u.category === 'Trust & Safety'),
                    ].map((item) => (
                      'excerpt' in item
                        ? <ResourceCard key={item.id} resource={item as Resource} />
                        : <HubCard key={item.id} card={item as HubCard} />
                    ))}
                  </div>
                </div>
              )}

              {/* Community Channels */}
              {showCommunity && (
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <Gift size={16} style={{ color: '#9B7FFF' }} />
                    <h2 className="font-display font-semibold text-[17px]" style={{ color: '#F2F4FF' }}>
                      Community Channels
                    </h2>
                    <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.06)' }} />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {[
                      ...mockResources.filter((r) => r.category === 'Community Channels'),
                      ...utilityLinks.filter((u) => u.category === 'Community Channels'),
                    ].map((item) => (
                      'excerpt' in item
                        ? <ResourceCard key={item.id} resource={item as Resource} />
                        : <HubCard key={item.id} card={item as HubCard} />
                    ))}
                  </div>
                </div>
              )}

              {/* About This Platform */}
              {showAbout && (
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <MapPin size={16} style={{ color: '#F0A832' }} />
                    <h2 className="font-display font-semibold text-[17px]" style={{ color: '#F2F4FF' }}>
                      About This Platform
                    </h2>
                    <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.06)' }} />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {[
                      ...mockResources.filter((r) => r.category === 'About This Platform'),
                      ...utilityLinks.filter((u) => u.category === 'About This Platform'),
                    ].map((item) => (
                      'excerpt' in item
                        ? <ResourceCard key={item.id} resource={item as Resource} />
                        : <HubCard key={item.id} card={item as HubCard} />
                    ))}
                  </div>
                </div>
              )}

            </div>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
